import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getAnalytics, isSupported } from 'firebase/analytics';
import { getAuth } from 'firebase/auth';
import { initializeFirestore, doc, getDoc } from 'firebase/firestore';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import UpcomingEvents from './components/UpcomingEvents';
import About from './components/About';
import SignaturePractice from './components/SignaturePractice';
import HealingSounds from './components/HealingSounds';
import HowItWorks from './components/HowItWorks';
import Schedule from './components/Schedule';
import AboutMe from './components/AboutMe';
import Testimonials from './components/Testimonials';
import Gallery from './components/Gallery';
import Reservation from './components/Reservation';
import Contact from './components/Contact';
import Footer from './components/Footer';
import Rules from './components/Rules';
import Blog from './components/Blog';
import Panel from './components/Panel';
import AdminPage from './components/AdminPage';
import SinglePostPage from './components/SinglePostPage';
import Videos from './components/Videos';
import Stories from './components/Stories';
import SingleStoryPage from './components/SingleStoryPage';
import SummerChallenge from './components/SummerChallenge';
import './design-system.css';

const firebaseConfig = {
  apiKey: "AIzaSyD8u5ysV3aSJcAgh98TkyuSLWadxAGmKSQ",
  authDomain: "cchi-kung.firebaseapp.com",
  projectId: "cchi-kung",
  storageBucket: "cchi-kung.firebasestorage.app",
  messagingSenderId: "889989547417",
  appId: "1:889989547417:web:7c298f0b46074662d0ff08",
  measurementId: "G-TD7T40HRMF"
};

// Initialize Firebase here, outside of the React component, so it only runs once.
const app = initializeApp(firebaseConfig);
// Avoid streaming Listen requests being buffered or rejected by browsers/proxies.
// This applies to every page that shares this Firestore instance.
const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
});
const auth = getAuth(app);
isSupported().then(supported => { if (supported) getAnalytics(app); }).catch(() => {});


const contentProps = { db, auth, appId: firebaseConfig.appId, initialAuthToken: null };
const titles = { '/': 'Qigong v Plzni', '/blog': 'Blog', '/stories': 'Zahrada příběhů', '/videa': 'Videa', '/galerie': 'Fotogalerie', '/rules': 'Pravidla studia', '/letni-vyzva': 'Letní výzva' };

function PublicLayout() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        document.title = `${titles[pathname] || 'Qigong'} | Tomáš Číhal`;
        if (hash) {
            document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'instant' });
            return;
        }
        window.scrollTo(0, 0);
    }, [pathname, hash]);
    return <div className={`public-site ${pathname === '/' ? 'page-home' : 'page-inner'}`}>
        <Navbar /><main id="main-content" tabIndex="-1"><Outlet /></main><Footer />
    </div>;
}

function Home() {
    return <><Hero /><UpcomingEvents /><AboutMe /><About /><SignaturePractice /><HealingSounds /><HowItWorks />
        <Schedule db={db} /><Testimonials {...contentProps} /><Reservation db={db} auth={auth} /><Contact /></>;
}

function ContentListing({ stories = false }) {
    const navigate = useNavigate();
    return stories ? <Stories {...contentProps} onStorySelect={story => navigate(`/stories/${encodeURIComponent(story.id)}`)} />
        : <Blog {...contentProps} onPostSelect={post => navigate(`/blog/${encodeURIComponent(post.id)}`)} />;
}

function ContentDetail({ stories = false }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true });
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        let disposed = false;
        setState({ loading: true });
        if (!id || id.includes('/')) { setState({ missing: true }); return; }
        const path = `/artifacts/${firebaseConfig.appId}/public/data/${stories ? 'stories' : 'blogPosts'}`;
        getDoc(doc(db, path, id)).then(snapshot => {
            if (!disposed) {
                setState(snapshot.exists() ? { content: { ...snapshot.data(), id: snapshot.id } } : { missing: true });
                if (snapshot.exists()) document.title = `${snapshot.data().title} | Tomáš Číhal`;
            }
        }).catch(() => { if (!disposed) setState({ error: true }); });
        return () => { disposed = true; };
    }, [id, stories, attempt]);
    if (state.loading) return <div className="page-state" role="status">Načítám {stories ? 'příběh' : 'článek'}…</div>;
    if (state.error) return <div className="page-state" role="alert"><h1>Obsah se nepodařilo načíst.</h1><p>Zkontrolujte připojení a zkuste to znovu.</p><button className="button button-primary" onClick={() => setAttempt(n => n + 1)}>Zkusit znovu</button></div>;
    if (state.missing) return <div className="page-state"><h1>Tento příspěvek tu už není.</h1><Link className="text-link" to={stories ? '/stories' : '/blog'}>Zpět na přehled →</Link></div>;
    const onBack = () => navigate(stories ? '/stories' : '/blog');
    return stories ? <SingleStoryPage story={state.content} onBack={onBack} db={db} appId={firebaseConfig.appId} />
        : <SinglePostPage post={state.content} onBack={onBack} db={db} appId={firebaseConfig.appId} />;
}

function AccountPage({ admin = false }) {
    const navigate = useNavigate();
    return <div className="account-page">{admin ? <AdminPage {...contentProps} onLogout={() => navigate('/')} /> : <Panel db={db} auth={auth} onLogout={() => navigate('/')} />}</div>;
}

export default function App() {
    return <BrowserRouter><Routes>
        <Route element={<PublicLayout />}>
            <Route index element={<Home />} />
            <Route path="blog" element={<ContentListing />} />
            <Route path="blog/:id" element={<ContentDetail />} />
            <Route path="stories" element={<ContentListing stories />} />
            <Route path="stories/:id" element={<ContentDetail stories />} />
            <Route path="videa" element={<Videos />} />
            <Route path="galerie" element={<Gallery />} />
            <Route path="rules" element={<Rules />} />
            <Route path="letni-vyzva" element={<SummerChallenge />} />
            <Route path="*" element={<div className="page-state"><p className="eyebrow">404</p><h1>Tady cesta nepokračuje.</h1><p>Požadovaná stránka neexistuje.</p><Link to="/" className="button button-primary">Zpět na úvod</Link></div>} />
        </Route>
        <Route path="manage" element={<AccountPage admin />} />
        <Route path="panel" element={<AccountPage />} />
    </Routes></BrowserRouter>;
}
